import { ChevronDown, ExternalLink, Pencil, Phone } from "lucide-react";
import {
  Fragment,
  useId,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";

import { SearchField } from "../../components/DataKit";
import { RegistrationEditor } from "../../components/GuestForm";
import { friendlyError } from "../../lib/friendlyError";
import { trpc } from "../../trpc";

import { GuestEmailActions, PaymentStatus } from "./PaymentActions";
import {
  arrivalOf,
  clockLabel,
  extrasOf,
  paidByLabel,
  partySizeLabel,
  peopleCount,
  placeTypeOf,
  receptionLinkFor,
  signedUpLabel,
  whatsappLabel,
} from "./guestFormat";
import { isMarkedNotComing, isNotComing, isSeva } from "./notComing";
import {
  lastHawanLabel,
  NEW_WINDOW_MS,
  partyMemberLabel,
  partyTags,
  sourceLabel,
  type PartyMember,
  type RosterRow,
} from "./shared";

/** How the guest list is ordered. */
const SORT_ORDERS = ["newest", "name", "largest"] as const;
type SortOrder = (typeof SORT_ORDERS)[number];

const SORT_LABEL = {
  newest: "Newest first",
  name: "Name",
  largest: "Largest party",
} satisfies Record<SortOrder, string>;

const byName = (a: RosterRow, b: RosterRow) =>
  a.name.localeCompare(b.name, "en", { sensitivity: "base" });

const COMPARE = {
  newest: (a, b) =>
    Date.parse(b.createdAt) - Date.parse(a.createdAt) || byName(a, b),
  name: byName,
  largest: (a, b) => b.totalPeople - a.totalPeople || byName(a, b),
} satisfies Record<SortOrder, (a: RosterRow, b: RosterRow) => number>;

const CHIP =
  "inline-flex min-h-11 items-center rounded-full border px-4 text-[14px] font-medium transition-colors";

/** Go to Reception's deep link inside the app. A click with a modifier
 *  key keeps the browser's own behaviour (a new tab). */
function openAtReception(event: MouseEvent<HTMLAnchorElement>, href: string) {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
    return;
  }
  event.preventDefault();
  window.history.pushState({}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/** "Heard about us" in words; "Not given" when nothing was recorded. */
function heardAboutUsText(r: RosterRow): string {
  return r.heardAboutUs || r.heardAboutUsDetail
    ? sourceLabel(r.heardAboutUs, r.heardAboutUsDetail)
    : "Not given";
}

function NotGiven() {
  return (
    <span className="text-[14px] text-[color:var(--color-ink-faint)]">
      Not given
    </span>
  );
}

/** Names under the booking, each with age and the needs the desk plans by. */
function PartyNames({ party }: { party: readonly PartyMember[] }) {
  return (
    <ul className="mt-1 space-y-1">
      {party.map((m, i) => (
        <li
          key={`${m.name}-${i}`}
          className="flex flex-wrap items-center gap-1.5"
        >
          <span>{partyMemberLabel(m)}</span>
          {partyTags(m).map((tag) => (
            <span
              key={tag}
              className="badge border border-[color:var(--color-amber)]/45 text-[color:var(--color-amber)]"
            >
              {tag}
            </span>
          ))}
        </li>
      ))}
    </ul>
  );
}

/** The name, its New, Walk-in and Not coming tags, and "4 people"
 *  underneath. */
function NameBlock({ r, fresh }: { r: RosterRow; fresh: boolean }) {
  return (
    <div>
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-[16px] font-semibold text-[color:var(--color-ink)]">
          {r.name}
        </span>
        {fresh ? (
          <span className="badge bg-[color:var(--color-amber)]/15 text-[color:var(--color-amber)]">
            New
          </span>
        ) : null}
        {r.isWalkIn ? (
          <span className="badge border border-[color:var(--color-line-strong)] text-[color:var(--color-ink-soft)]">
            Walk-in
          </span>
        ) : null}
        {isNotComing(r) ? (
          <span className="badge border border-[color:var(--color-line-strong)] text-[color:var(--color-ink-soft)]">
            {isSeva(r) ? "Not coming · Seva" : "Not coming"}
          </span>
        ) : null}
      </p>
      <p className="mt-0.5 text-[14px] text-[color:var(--color-ink-soft)] tabular-nums">
        {peopleCount(r.totalPeople)}
      </p>
    </div>
  );
}

/** Arrived, part of the party here, not coming, or not yet with a way to
 *  check in. */
function ArrivedBlock({ r }: { r: RosterRow }) {
  const arrival = arrivalOf(r);
  if (arrival.kind === "arrived") {
    return (
      <p className="text-[14px] font-medium text-[color:var(--color-success)]">
        Checked in at {clockLabel(arrival.at)}
      </p>
    );
  }
  // Nothing to check in. If they turn up after all, Reception still
  // finds them.
  if (isNotComing(r)) {
    return (
      <p className="text-[14px] text-[color:var(--color-ink-faint)]">
        Not coming
      </p>
    );
  }
  const href = receptionLinkFor(r);
  return (
    <div className="flex items-center gap-x-3">
      <p className="text-[14px] text-[color:var(--color-ink-soft)]">
        {arrival.kind === "partly"
          ? `${arrival.here} of ${arrival.of} here`
          : "Not yet"}
      </p>
      <a
        href={href}
        onClick={(event) => openAtReception(event, href)}
        aria-label={`Check in ${r.name} at Reception`}
        className="inline-flex min-h-11 items-center text-[14px] font-semibold text-[color:var(--color-amber)] underline underline-offset-4"
      >
        Check in
      </a>
    </div>
  );
}

/** Opens and closes one guest's details. */
function DetailsToggle({
  name,
  open,
  controls,
  onToggle,
}: {
  name: string;
  open: boolean;
  controls: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={controls}
      className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-[color:var(--color-line-strong)] px-3.5 text-[14px] font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]"
    >
      Details
      <span className="sr-only"> for {name}</span>
      <ChevronDown
        size={16}
        aria-hidden="true"
        className={`transition-transform ${open ? "rotate-180" : ""}`}
      />
    </button>
  );
}

/** One line of a guest's details: "Signed up: 1 Sep, 9:05 am". */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="inline text-[color:var(--color-ink-soft)]">{label}: </dt>
      <dd className="inline text-[color:var(--color-ink)] [overflow-wrap:anywhere]">
        {children}
      </dd>
    </div>
  );
}

/**
 * Everything else about one guest, one tap away: contact, party, answers
 * from the form, how they paid, and the emails the desk can send. Long
 * notes wrap here, inside the panel, so the table never grows sideways.
 */
const EDIT_BUTTON =
  "inline-flex min-h-11 items-center justify-center gap-1.5 rounded-md border border-[color:var(--color-line-strong)] px-3.5 text-[14px] font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]";

function GuestDetails({ r, id }: { r: RosterRow; id: string }) {
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  // Pending until the list has refreshed, so the button can't be pressed
  // again while it still shows the old choice.
  const setNotComing = trpc.adminSetNotComing.useMutation({
    onSuccess: () => utils.adminListEventRegistrations.invalidate(),
  });
  if (editing) {
    return (
      <div id={id} className="flex flex-col gap-4">
        <RegistrationEditor
          registration={r}
          intro="Change their name, email, phone or how many people are coming."
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            setSaved(true);
            void utils.adminListEventRegistrations.invalidate();
          }}
        />
      </div>
    );
  }
  const lastHawan = lastHawanLabel(r.lastHawan);
  const whatsapp = whatsappLabel(r);
  const paid = r.bookingType === "paid";
  const placeType = paid ? placeTypeOf(r) : null;
  const extras = paid ? extrasOf(r) : null;
  const paidBy = paidByLabel(r);
  // Only while no one on the booking has arrived: once someone has, they
  // are coming whatever the mark says, so the button would change nothing.
  const canSetNotComing = arrivalOf(r).kind === "not-yet";
  const markedNotComing = isMarkedNotComing(r);
  return (
    <div id={id} className="flex flex-col gap-4">
      <dl className="grid gap-x-8 gap-y-2 text-[14px] sm:grid-cols-2 xl:grid-cols-3">
        <Field label="Email">
          {r.email ? (
            <a
              href={`mailto:${r.email}`}
              className="underline underline-offset-4"
            >
              {r.email}
            </a>
          ) : (
            <NotGiven />
          )}
        </Field>
        <Field label="People">
          {partySizeLabel(r)}
          {r.party.length > 0 ? <PartyNames party={r.party} /> : null}
        </Field>
        {r.groupName ? <Field label="Group">{r.groupName}</Field> : null}
        {r.dietaryRequirements ? (
          <Field label="Dietary needs">{r.dietaryRequirements}</Field>
        ) : null}
        {r.registrantNote ? (
          <Field label="Note to organisers">{r.registrantNote}</Field>
        ) : null}
        <Field label="Heard about us">{heardAboutUsText(r)}</Field>
        {lastHawan ? <Field label="Last Hawan">{lastHawan}</Field> : null}
        {whatsapp ? <Field label="WhatsApp group">{whatsapp}</Field> : null}
        {placeType ? <Field label="Type of place">{placeType}</Field> : null}
        {extras ? <Field label="Extras">{extras}</Field> : null}
        {/* A gifted place already says Gifted in its payment status. */}
        {paid && r.paymentMethod !== "gifted" ? (
          <Field label="Paid by">
            {paidBy ?? (
              <span className="text-[color:var(--color-ink-faint)]">
                Not recorded
              </span>
            )}
            {r.receiptUrl ? (
              <>
                {" · "}
                <a
                  href={r.receiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center gap-1 underline underline-offset-4"
                >
                  Receipt
                  <ExternalLink size={14} aria-hidden="true" />
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
              </>
            ) : null}
          </Field>
        ) : null}
        <Field label="Signed up">
          <span className="tabular-nums">{signedUpLabel(r.createdAt)}</span>
        </Field>
        <Field label="Reference">
          <span className="font-mono">{r.confirmationRef}</span>
        </Field>
      </dl>
      <div className="flex flex-col items-start gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => {
              setSaved(false);
              setEditing(true);
            }}
            className={EDIT_BUTTON}
          >
            <Pencil size={14} aria-hidden="true" />
            Edit details
          </button>
          {canSetNotComing ? (
            <button
              type="button"
              onClick={() => {
                setSaved(false);
                setNotComing.mutate({
                  id: r.id,
                  notComing: !markedNotComing,
                });
              }}
              disabled={setNotComing.isPending}
              className={`${EDIT_BUTTON} disabled:opacity-50`}
            >
              {setNotComing.isPending
                ? "Saving…"
                : markedNotComing
                  ? "They're coming after all"
                  : "Mark as not coming"}
            </button>
          ) : null}
          {saved ? (
            <p
              role="status"
              className="text-[14px] font-medium text-[color:var(--color-success)]"
            >
              Saved.
            </p>
          ) : null}
        </div>
        {canSetNotComing && !markedNotComing && isSeva(r) ? (
          <p className="text-[14px] text-[color:var(--color-ink-soft)]">
            Their payment stays recorded as seva, and their place is freed for
            someone else.
          </p>
        ) : null}
        {setNotComing.error ? (
          <p
            role="alert"
            className="text-[14px] text-[color:var(--color-danger)]"
          >
            {friendlyError(setNotComing.error)}
          </p>
        ) : null}
      </div>
      <GuestEmailActions r={r} />
    </div>
  );
}

/**
 * The guest list: search, sort, a filter or two when they help, and the
 * essentials for each guest (name, phone, payment, arrival) with the rest
 * behind Details. A table on wider screens and one card per booking on a
 * phone. Owns its own search and sort; the download in the page header
 * always takes everyone.
 */
export function RosterTable({
  rows,
  showEventColumn,
  isPaid,
  eventIsToday,
  now,
}: {
  rows: readonly RosterRow[];
  showEventColumn: boolean;
  isPaid: boolean;
  /** The event is on today, so the Not arrived filter is worth showing. */
  eventIsToday: boolean;
  now: number;
}) {
  const ids = useId();
  const headingId = `${ids}-heading`;
  const sortId = `${ids}-sort`;
  const [search, setSearch] = useState("");
  const [onlyNotArrived, setOnlyNotArrived] = useState(false);
  const [onlyWhatsApp, setOnlyWhatsApp] = useState(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");
  // One guest's details open at a time.
  const [openId, setOpenId] = useState<string | null>(null);
  // Details once opened stay mounted, hidden when closed, so an email
  // still sending can say "Email sent" (or fail) when they are reopened.
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());

  const anyoneArrived = rows.some((r) => arrivalOf(r).kind !== "not-yet");
  const wantsWhatsApp = rows.filter(
    (r) => whatsappLabel(r) === "Add to WhatsApp group",
  ).length;

  // Name, Event (All events), Phone, Payment (paid events), Arrived, Details.
  const columnCount = 4 + (showEventColumn ? 1 : 0) + (isPaid ? 1 : 0);

  const needle = search.trim().toLowerCase();
  const filtering = needle !== "" || onlyNotArrived || onlyWhatsApp;

  const visible = useMemo(() => {
    const digits = needle.replace(/\D/g, "");
    const filtered = rows.filter((r) => {
      // Not arrived is who is still expected, so not a booking marked not
      // coming.
      if (
        onlyNotArrived &&
        (arrivalOf(r).kind === "arrived" || isNotComing(r))
      ) {
        return false;
      }
      if (onlyWhatsApp && whatsappLabel(r) !== "Add to WhatsApp group") {
        return false;
      }
      if (!needle) {
        return true;
      }
      const text = [
        r.name,
        r.email,
        r.confirmationRef,
        r.phone ?? "",
        r.dietaryRequirements ?? "",
        r.registrantNote ?? "",
        r.groupName ?? "",
        r.heardAboutUsDetail ?? "",
        ...r.party.map((m) => m.name),
      ]
        .join(" ")
        .toLowerCase();
      if (text.includes(needle)) {
        return true;
      }
      // "0412 345 678" finds "+61412345678" typed without spaces.
      return (
        digits.length >= 3 &&
        (r.phone ?? "").replace(/\D/g, "").includes(digits)
      );
    });
    return [...filtered].sort(COMPARE[sortOrder]);
  }, [rows, needle, onlyNotArrived, onlyWhatsApp, sortOrder]);

  function showEveryone() {
    setSearch("");
    setOnlyNotArrived(false);
    setOnlyWhatsApp(false);
  }

  function toggleDetails(id: string) {
    setOpenId((current) => (current === id ? null : id));
    setOpened((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }

  const isFresh = (r: RosterRow) =>
    now - new Date(r.createdAt).getTime() < NEW_WINDOW_MS;

  return (
    <section aria-labelledby={headingId} className="mt-10">
      <h3
        id={headingId}
        className="font-display text-title text-[color:var(--color-ink)]"
      >
        Guest list ({rows.length})
      </h3>

      {rows.length > 0 ? (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Name, phone, email or reference"
          />
          <div className="flex items-center gap-2">
            <label
              htmlFor={sortId}
              className="text-[14px] font-semibold text-[color:var(--color-ink)]"
            >
              Sort by
            </label>
            <select
              id={sortId}
              value={sortOrder}
              onChange={(e) => {
                const next = SORT_ORDERS.find((o) => o === e.target.value);
                if (next) {
                  setSortOrder(next);
                }
              }}
              className="min-h-11 rounded-md border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] px-3 text-[16px] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-amber)]"
            >
              {SORT_ORDERS.map((order) => (
                <option key={order} value={order}>
                  {SORT_LABEL[order]}
                </option>
              ))}
            </select>
          </div>
          {/* All events mixes finished events in, where every no-show
              would count as not arrived. */}
          {eventIsToday || (anyoneArrived && !showEventColumn) ? (
            <button
              type="button"
              aria-pressed={onlyNotArrived}
              onClick={() => setOnlyNotArrived((v) => !v)}
              className={`${CHIP} ${
                onlyNotArrived
                  ? "border-[color:var(--color-ink)] bg-[color:var(--color-ink)] text-[color:var(--color-paper)]"
                  : "border-[color:var(--color-line-strong)] text-[color:var(--color-ink)]"
              }`}
            >
              Not arrived
            </button>
          ) : null}
          {wantsWhatsApp > 0 ? (
            <button
              type="button"
              aria-pressed={onlyWhatsApp}
              onClick={() => setOnlyWhatsApp((v) => !v)}
              className={`${CHIP} ${
                onlyWhatsApp
                  ? "border-[color:var(--color-ink)] bg-[color:var(--color-ink)] text-[color:var(--color-paper)]"
                  : "border-[color:var(--color-line-strong)] text-[color:var(--color-ink)]"
              }`}
            >
              Wants WhatsApp group ({wantsWhatsApp})
            </button>
          ) : null}
        </div>
      ) : null}

      <p
        aria-live="polite"
        className="mt-2 text-[14px] text-[color:var(--color-ink-soft)] tabular-nums"
      >
        {filtering && rows.length > 0
          ? `Showing ${visible.length} of ${rows.length}`
          : ""}
      </p>

      {rows.length === 0 ? (
        <p className="empty-state">
          {showEventColumn
            ? "No one has booked yet."
            : "No one has booked for this event yet."}
        </p>
      ) : visible.length === 0 ? (
        <div className="empty-state">
          <p>
            {needle
              ? `No one matches "${search.trim()}".`
              : "No one matches these filters."}
          </p>
          <button
            type="button"
            onClick={showEveryone}
            className="mt-3 inline-flex min-h-11 items-center rounded-md border border-[color:var(--color-line-strong)] px-4 text-[15px] font-semibold text-[color:var(--color-ink)]"
          >
            {needle ? "Clear search" : "Show everyone"}
          </button>
        </div>
      ) : (
        <>
          {/* Phones: one card per booking, in the table's order. */}
          <ul aria-label="Guests" className="mt-2 space-y-3 md:hidden">
            {visible.map((r) => {
              const open = openId === r.id;
              const detailsId = `${ids}-card-${r.id}`;
              return (
                <li key={r.id} className="surface p-4">
                  {showEventColumn ? (
                    <p className="mb-1 text-[14px] text-[color:var(--color-ink-soft)]">
                      {r.eventTitle}
                    </p>
                  ) : null}
                  <NameBlock r={r} fresh={isFresh(r)} />
                  <div className="mt-3">
                    {r.phone ? (
                      <a
                        href={`tel:${r.phone.replace(/\s+/g, "")}`}
                        className="inline-flex min-h-11 items-center gap-2 rounded-md border border-[color:var(--color-line-strong)] px-4 text-[16px] font-semibold text-[color:var(--color-ink)]"
                      >
                        <Phone size={18} aria-hidden="true" />
                        Call {r.phone}
                      </a>
                    ) : (
                      <p className="text-[14px] text-[color:var(--color-ink-faint)]">
                        No phone number given
                      </p>
                    )}
                  </div>
                  {isPaid && r.bookingType != null ? (
                    <div className="mt-3">
                      <PaymentStatus r={r} />
                    </div>
                  ) : null}
                  <div className="mt-3 border-t border-[color:var(--color-line)] pt-2">
                    <ArrivedBlock r={r} />
                  </div>
                  <div className="mt-2">
                    <DetailsToggle
                      name={r.name}
                      open={open}
                      controls={detailsId}
                      onToggle={() => toggleDetails(r.id)}
                    />
                  </div>
                  {open || opened.has(r.id) ? (
                    <div
                      hidden={!open}
                      className="mt-3 border-t border-[color:var(--color-line)] pt-3"
                    >
                      <GuestDetails r={r} id={detailsId} />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {/* Wider screens: the table. */}
          <div className="mt-2 hidden overflow-x-auto md:block">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-[color:var(--color-line-strong)]">
                  <th
                    scope="col"
                    className="table-head py-2 pr-5 align-bottom whitespace-nowrap"
                  >
                    Name
                  </th>
                  {showEventColumn ? (
                    <th
                      scope="col"
                      className="table-head py-2 pr-5 align-bottom whitespace-nowrap"
                    >
                      Event
                    </th>
                  ) : null}
                  <th
                    scope="col"
                    className="table-head py-2 pr-5 align-bottom whitespace-nowrap"
                  >
                    Phone
                  </th>
                  {isPaid ? (
                    <th
                      scope="col"
                      className="table-head py-2 pr-5 align-bottom whitespace-nowrap"
                    >
                      Payment
                    </th>
                  ) : null}
                  <th
                    scope="col"
                    className="table-head py-2 pr-5 align-bottom whitespace-nowrap"
                  >
                    Arrived
                  </th>
                  <th scope="col" className="py-2 whitespace-nowrap">
                    <span className="sr-only">Details</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const open = openId === r.id;
                  const detailsId = `${ids}-row-${r.id}`;
                  return (
                    <Fragment key={r.id}>
                      <tr
                        className={`align-top transition-colors ${
                          open
                            ? "bg-[color:var(--color-paper)]"
                            : "border-b border-[color:var(--color-line)] hover:bg-[color:var(--color-paper)]"
                        }`}
                      >
                        <td className="py-3 pr-5 pl-2 whitespace-nowrap">
                          <NameBlock r={r} fresh={isFresh(r)} />
                        </td>
                        {showEventColumn ? (
                          <td className="py-3 pr-5 text-[14px] whitespace-nowrap text-[color:var(--color-ink-soft)]">
                            {r.eventTitle}
                          </td>
                        ) : null}
                        <td className="py-3 pr-5 text-[14px] whitespace-nowrap text-[color:var(--color-ink)]">
                          {r.phone ? (
                            <a
                              href={`tel:${r.phone.replace(/\s+/g, "")}`}
                              className="underline-offset-4 hover:underline"
                            >
                              {r.phone}
                            </a>
                          ) : (
                            <NotGiven />
                          )}
                        </td>
                        {isPaid ? (
                          <td className="py-3 pr-5 whitespace-nowrap">
                            <PaymentStatus r={r} />
                          </td>
                        ) : null}
                        <td className="py-3 pr-5 whitespace-nowrap">
                          <ArrivedBlock r={r} />
                        </td>
                        <td className="py-2 pr-2 text-right whitespace-nowrap">
                          <DetailsToggle
                            name={r.name}
                            open={open}
                            controls={detailsId}
                            onToggle={() => toggleDetails(r.id)}
                          />
                        </td>
                      </tr>
                      {open || opened.has(r.id) ? (
                        <tr
                          hidden={!open}
                          className="border-b border-[color:var(--color-line)] bg-[color:var(--color-paper)]"
                        >
                          <td colSpan={columnCount} className="px-2 pt-1 pb-5">
                            <GuestDetails r={r} id={detailsId} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
